import React, { useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { FiPackage, FiGrid, FiTruck, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";

import KPICard, { DashboardCard } from "../../Common/KPICard";
import SelectFilter from "../../Common/SelectFilter";
import DateRangeFilter from "../../../Common/DateFilter";
import ActionButton from "../../Common/ActionButton";
import StatusChip from "../../Common/StatusChip";
import "../../../dashboardLayout.css";
import useReportData from "../useReportData";
import { pkrShort, pkrFull, withBars, dmy } from "../dashboardFormat";

const theme = {
  primary: "#0b6462",
  primaryLight: "#eefbfb",
  secondary: "#64748b",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#38bdf8",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  border: "#e2e8f0",
  surface: "#ffffff",
  shadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
  shadowHover: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { y: 15, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 110 } },
};

const EMPTY_DASH = { projects: [], kpis: {}, locations: [], movements: [], aging: [], recent: [] };
const LOCATION_COLORS = ["#22c55e", "#38bdf8", "#f59e0b", "#f87171", "#a855f7", "#14b8a6"];
const MOVEMENT_COLORS = ["#22c55e", "#2563eb"];
const AGING_COLORS = ["#22c55e", "#38bdf8", "#f59e0b", "#f87171"];

const StoreDashboard = () => {
  const history = useHistory();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [filterDates, setFilterDates] = useState({ from: "", to: "" });
  const params = useMemo(
    () => ({
      projectId: selectedLocation || undefined,
      from: filterDates.from || undefined,
      to: filterDates.to || undefined,
    }),
    [selectedLocation, filterDates],
  );
  const [dash] = useReportData("StoreDashboard", EMPTY_DASH, params);
  const STORE_LOCATIONS = useMemo(() => withBars(dash.locations, LOCATION_COLORS), [dash]);
  const STORE_MOVEMENTS = useMemo(() => withBars(dash.movements, MOVEMENT_COLORS), [dash]);
  const MATERIAL_AGING = useMemo(() => withBars(dash.aging, AGING_COLORS), [dash]);
  const RECENT_STORE_TRANSACTIONS = useMemo(
    () =>
      dash.recent.map((t) => ({
        ...t,
        date: dmy(t.date),
        value: t.type === "Good Receipt" ? pkrFull(t.value) : "—",
        statusType: t.type === "Good Receipt" ? "success" : "info",
      })),
    [dash],
  );

  const kpis = [
    {
      title: "Total Inventory Value",
      value: pkrShort(dash.kpis.inventoryValue),
      subtext: "Remaining stock at cost",
      subtextColor: "#64748b",
      icon: FiPackage,
      color: "#0b6462",
      onClick: () => history.push("/store/stock"),
    },
    {
      title: "Total SKU Items",
      value: String(dash.kpis.skuCount ?? 0),
      subtext: "Across all active project stores",
      subtextColor: "#64748b",
      icon: FiGrid,
      color: "#0284c7",
      onClick: () => history.push("/store/stock"),
    },
    {
      title: "Items Issued (to Site)",
      value: `${Number(dash.kpis.issuedQty || 0).toLocaleString()} Units`,
      subtext: `In the selected period, valued at ${pkrShort(dash.kpis.issuedValue)}`,
      subtextColor: "#64748b",
      icon: FiTruck,
      color: "#2563eb",
      onClick: () => history.push("/procurement/purchase-requisition"),
    },
    {
      title: "Critical Low Stock / Alerts",
      value: String(dash.kpis.lowStockCount ?? 0),
      subtext: "Items below safety threshold",
      subtextColor: "#ef4444",
      icon: FiAlertTriangle,
      color: "#ef4444",
      onClick: () => history.push("/store/stock"),
    },
  ];

  const handleDateRangeChange = useCallback((range) => {
    setFilterDates(range);
  }, []);

  const handleGenerateReport = () => {
    const rangeText =
      filterDates.from && filterDates.to ? ` for ${filterDates.from} to ${filterDates.to}` : "";
    toast.info(`Generating Store Inventory & Valuation Report${rangeText}...`);
    history.push("/store/material/stock-report");
  };

  return (
    <div className="pt-2 app_font pb-5">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between mb-3 app_font"
        style={{ gap: "12px" }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Store Inventory & Warehouse Dashboard
          </h4>
        </div>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
          <SelectFilter
            id="storeLocationFilter"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            label=""
            defaultOptionLabel="All Project Stores & Sites"
            maxWidth="240px"
            options={dash.projects.map((p) => ({ value: p.id, label: p.name }))}
          />

          <div className="d-flex align-items-center" style={{ width: "230px" }}>
            <div className="floating-field" style={{ width: "100%" }}>
              <DateRangeFilter onChange={handleDateRangeChange} />
              <label>Date Range</label>
            </div>
          </div>

          <ActionButton text="Generate Report" variant="save" onClick={handleGenerateReport} />
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="erp-dashboard-wrap"
      >
        {/* ── Row 1: KPI Summary Cards ── */}
        <div className="erp-kpi-grid">
          {kpis.map((kpi, idx) => (
            <div
              key={idx}
              onClick={kpi.onClick}
              style={{ cursor: kpi.onClick ? "pointer" : "default" }}
            >
              <KPICard
                title={kpi.title}
                value={kpi.value}
                subtitle={kpi.subtext}
                icon={kpi.icon}
                themeColor={kpi.color}
                themeBgColor={`${kpi.color}15`}
                subtitleColor={kpi.subtextColor}
                itemVariants={itemVariants}
              />
            </div>
          ))}
        </div>

        {/* ── Row 2: Stock Value by Location & Store Movements (MTDs) ── */}
        <div className="erp-two-col-grid">
          {/* Left: Stock Value by Location / Project Store */}
          <DashboardCard
            title="Stock Value by Location / Project Store"
            subtitle="Breakdown of progressive spend"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "18px", minHeight: "260px" }}
            >
              {STORE_LOCATIONS.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "190px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.name}
                    >
                      {item.name}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Right: Store Movements & Transactions (MTDs) */}
          <DashboardCard
            title="Store Movements & Transactions (MTDs)"
            subtitle="Comparing major transactional types"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "18px", minHeight: "260px" }}
            >
              {STORE_MOVEMENTS.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "190px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.type}
                    >
                      {item.type}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 3: Material Aging & Recent Materials Movement ── */}
        <div className="erp-two-col-grid erp-mb-0">
          {/* Left: Material Aging & Non-Moving Stock */}
          <DashboardCard
            title="Material Aging & Non-Moving Stock (Days)"
            subtitle="Stock movement pace and aging distribution"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "18px", minHeight: "260px" }}
            >
              {MATERIAL_AGING.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "190px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Right: Recent Materials Received (GRN) & Issued (MIN) */}
          <DashboardCard
            title="Recent Materials Received (GRN) & Issued (MIN)"
            subtitle="Latest store movement entries"
            theme={theme}
            itemVariants={itemVariants}
            action={
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                style={{
                  fontSize: "12px",
                  color: "var(--color-primary, #0b6462)",
                  fontWeight: 600,
                }}
                onClick={() => history.push("/store/stock")}
              >
                View Full Stock
              </button>
            }
          >
            <div className="table-responsive" style={{ minHeight: "240px" }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: "12.5px" }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      color: "#64748b",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Tran No.</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Date</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Site Location</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Items Detail</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Value (PKR)</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, textAlign: "center" }}>
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {RECENT_STORE_TRANSACTIONS.map((tran) => (
                    <tr
                      key={tran.id}
                      style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                      onClick={() => history.push("/store/stock")}
                    >
                      <td
                        style={{
                          padding: "10px",
                          fontWeight: 600,
                          color: "var(--color-primary, #0b6462)",
                        }}
                      >
                        {tran.tranNo}
                      </td>
                      <td style={{ padding: "10px", color: "#64748b" }}>{tran.date}</td>
                      <td style={{ padding: "10px", fontWeight: 500, color: "#1e293b" }}>
                        {tran.location}
                      </td>
                      <td style={{ padding: "10px", color: "#475569" }}>{tran.items}</td>
                      <td style={{ padding: "10px", fontWeight: 600, color: "#0f172a" }}>
                        {tran.value}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        <StatusChip status={tran.type} type={tran.statusType} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DashboardCard>
        </div>
      </motion.div>
    </div>
  );
};

export default StoreDashboard;
